import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GoogleSignInButton, OrDivider } from "@/components/GoogleSignInButton";

const ROLE_LABELS = { admin: "Administrator", top_management: "Top Management", employee: "Employee" };

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invitations/${token}`);
        setInvite(data);
        setName(data.name || "");
      } catch (e) {
        setError(formatErr(e));
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/invitations/${token}/accept`, { password, name: name.trim() });
      setSession(data);
      toast.success(`Welcome, ${data.user.name}!`);
      nav("/");
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">Loading invitation…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md bg-white border border-rose-200 rounded-md p-6 text-center">
        <div className="text-lg font-semibold text-rose-700 mb-2">Invitation problem</div>
        <div className="text-sm text-slate-600 mb-4">{error}</div>
        <Button variant="outline" onClick={() => nav("/login")}>Go to sign-in</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50">
      <form data-testid="invite-accept-form" onSubmit={submit} className="w-full max-w-md bg-white border border-slate-200 rounded-md p-8">
        <img src="/kms-logo.png" alt="KMS" className="w-14 h-14 mx-auto mb-4 rounded-full object-contain" />
        <div className="text-center">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Invitation from {invite.invited_by_name}</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">Join KMS-Obligo</h1>
          <p className="text-sm text-slate-500 mt-2">Set a password to access your <b>{ROLE_LABELS[invite.role]}</b> account for <span className="mono">{invite.email}</span>.</p>
        </div>
        <div className="space-y-3 mt-6">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Full name</Label>
            <Input data-testid="accept-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" required />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Password</Label>
            <Input data-testid="accept-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 mono" required minLength={6} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Confirm password</Label>
            <Input data-testid="accept-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 mono" required minLength={6} />
          </div>
        </div>
        <Button data-testid="accept-submit" type="submit" disabled={submitting} className="w-full mt-6 bg-blue-700 hover:bg-blue-800 text-white">
          {submitting ? "Creating account…" : "Accept & sign in"}
        </Button>
        <OrDivider />
        <GoogleSignInButton testid="invite-google-btn" returnPath={`/invite/${token}`} label="Accept with Google" />
      </form>
    </div>
  );
}
